/**
 * Mobile Travel Request — submit a travel request from the phone.
 *
 * Backend: POST /api/hr/travel/requests (existing, reused — no new endpoint).
 * Approval flow: handled by the existing hr_travel.py state machine
 * (request_submitted → approved/rejected → expenses → paid).
 *
 * Change 19 — Phase B of the Consultant employee-type rollout, but this
 * screen is visible to ALL employees, not just consultants. Mobile UI gap
 * we're filling: travel requests only had a web UI before.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Plane, Send, Loader2, Briefcase } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';

const MODES = [
  { v: 'flight', label: 'Flight' },
  { v: 'train', label: 'Train' },
  { v: 'bus', label: 'Bus' },
  { v: 'own_vehicle', label: 'Own vehicle' },
  { v: 'company_vehicle', label: 'Company vehicle' },
  { v: 'taxi', label: 'Taxi' },
];

export default function MobileTravelRequest() {
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    request_type: 'domestic',
    purpose: '',
    project_id: '',
    from_location: '',
    to_location: '',
    departure_date: '',
    return_date: '',
    mode: 'flight',
    estimated_cost: '',
    advance_requested: '',
    accommodation_required: false,
  });

  useEffect(() => {
    apiGet('/projects', { active_only: true })
      .then(d => setProjects(Array.isArray(d) ? d : (d?.items || [])))
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.purpose.trim()) return toast.error('Purpose is required');
    if (!form.to_location.trim()) return toast.error('Destination is required');
    if (!form.departure_date) return toast.error('Departure date is required');
    if (form.return_date && form.return_date < form.departure_date) {
      return toast.error('Return date must be after departure');
    }
    setSubmitting(true);
    try {
      // Create then submit (move from draft → pending_approval) in one go
      const payload = {
        ...form,
        estimated_cost: form.estimated_cost === '' ? null : Number(form.estimated_cost),
        advance_requested: form.advance_requested === '' ? 0 : Number(form.advance_requested),
        project_id: form.project_id || null,
      };
      const created = await apiPost('/hr/travel/requests', payload);
      // Auto-submit so the request reaches the approver immediately
      try { await apiPost(`/hr/travel/requests/${created.id}/submit`, {}); } catch { /* draft is still fine */ }
      toast.success('Travel request submitted');
      nav('/m');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit travel request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="mobile-travel-request" className="min-h-screen bg-slate-50 pb-24">
      <header className="bg-[#3b4872] text-white px-4 py-3 sticky top-0 z-10 flex items-center gap-3">
        <Link to="/m" className="p-1 hover:bg-white/10 rounded-sm" data-testid="back-home">
          <ArrowLeft size={20} />
        </Link>
        <Plane size={20} />
        <div className="flex-1">
          <div className="font-display font-bold text-base">Travel Request</div>
          <div className="text-[10px] text-white/70">Goes to your reporting manager for approval</div>
        </div>
      </header>

      <div className="p-4 space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Trip Type</span>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {['domestic', 'international'].map(t => (
              <button key={t} type="button" data-testid={`type-${t}`}
                onClick={() => set('request_type', t)}
                className={`py-3 text-sm font-bold uppercase tracking-wider border rounded-sm ${
                  form.request_type === t ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white border-slate-300 text-slate-700'
                }`}>
                {t}
              </button>
            ))}
          </div>
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Purpose <span className="text-red-500">*</span></span>
          <textarea data-testid="travel-purpose" value={form.purpose}
            onChange={e => set('purpose', e.target.value)}
            placeholder="e.g. Site inspection — Phase 2 kick-off"
            rows={2}
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Project (optional)</span>
          <select data-testid="travel-project" value={form.project_id}
            onChange={e => set('project_id', e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
            <option value="">— Not project-specific —</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">From</span>
            <input data-testid="travel-from" value={form.from_location}
              onChange={e => set('from_location', e.target.value)}
              placeholder="e.g. Mumbai"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">To <span className="text-red-500">*</span></span>
            <input data-testid="travel-to" value={form.to_location}
              onChange={e => set('to_location', e.target.value)}
              placeholder="e.g. Pune"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Departure <span className="text-red-500">*</span></span>
            <input data-testid="travel-departure" type="date" value={form.departure_date}
              onChange={e => set('departure_date', e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Return</span>
            <input data-testid="travel-return" type="date" value={form.return_date}
              onChange={e => set('return_date', e.target.value)}
              min={form.departure_date || undefined}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </label>
        </div>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Mode</span>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {MODES.map(m => (
              <button key={m.v} type="button" data-testid={`mode-${m.v}`}
                onClick={() => set('mode', m.v)}
                className={`py-2 text-xs font-bold uppercase tracking-wider border rounded-sm ${
                  form.mode === m.v ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white border-slate-300 text-slate-700'
                }`}>
                {m.label}
              </button>
            ))}
          </div>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Est. Cost (₹)</span>
            <input data-testid="travel-est-cost" type="number" min="0"
              value={form.estimated_cost}
              onChange={e => set('estimated_cost', e.target.value)}
              placeholder="0"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Advance (₹)</span>
            <input data-testid="travel-advance" type="number" min="0"
              value={form.advance_requested}
              onChange={e => set('advance_requested', e.target.value)}
              placeholder="0"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </label>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" data-testid="travel-accommodation"
            checked={form.accommodation_required}
            onChange={e => set('accommodation_required', e.target.checked)}
            className="w-4 h-4" />
          Accommodation required
        </label>

        <button data-testid="travel-submit" onClick={submit} disabled={submitting}
          className="w-full py-4 bg-[#3b4872] hover:bg-[#2d3858] text-white font-display font-bold uppercase tracking-wider text-sm rounded-sm flex items-center justify-center gap-2 disabled:opacity-50">
          {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          {submitting ? 'Submitting…' : 'Submit Travel Request'}
        </button>

        <p className="text-[11px] text-slate-500 leading-snug">
          Your request goes to your reporting manager. Once approved, you can claim
          expenses by uploading bills from the same flow on the web — or use{' '}
          <Briefcase size={11} className="inline" /> Petty Cash for small reimbursements.
        </p>
      </div>
    </div>
  );
}
